import { EVENT_QUERY_PARAM } from '@/features/events/hooks/useEditEventModal';
import { Role } from '@/types/gql.generated';

// The server exposes this same trio on both EventOrigin (event cards, edit
// modal) and ExperienceFeed (Sources panel), so one rule covers every link that
// points from one experience to another.
export type OriginLinkTarget = {
  editUrl?: string | null;
  publicUrl?: string | null;
  role?: Role | null;
};

const EDIT_ROLES: Role[] = [Role.Editor, Role.Owner];

// Where to send someone following a link to another experience.
//
// The server only fills in publicUrl when the origin has an enabled public
// slug, so preference alone isn't enough — the rule has to fall back to
// whatever the caller can actually open:
//
//   EDITOR / OWNER  edit route, falling back to the public page
//   VIEWER          public page, falling back to the edit route
//   no grant        public page only
//
// VIEWER reaches the edit route too: getExperience is gated at VIEWER and the
// /experience/:id/edit route has no role gate of its own, which is the same
// reasoning that already sends view-only collaborators there for planners (see
// LibraryItemCard). It just shouldn't be preferred over the published page when
// there is one. Without that fallback, a private experience shared read-only —
// a common way to add a source — links nowhere at all.
//
// Null means neither route is reachable: an unpublished origin the caller has
// no grant on. Callers must render the label unlinked rather than guess a URL
// that would land on an access-denied screen.
//
// The role is the caller's on the *origin*, not on the experience they're
// currently in: editing a schedule says nothing about the schedules it pulls
// from.
export const getOriginUrl = ({
  editUrl,
  publicUrl,
  role,
}: OriginLinkTarget): string | null => {
  if (role && EDIT_ROLES.includes(role)) {
    return editUrl ?? publicUrl ?? null;
  }
  if (role === Role.Viewer) {
    return publicUrl ?? editUrl ?? null;
  }
  return publicUrl ?? null;
};

/**
 * The same destination, deep-linked to one event rather than to the origin's
 * page as a whole.
 *
 * Both destinations getOriginUrl can return read `?event=` and open that event's
 * modal (useQueryEvent, mounted by the edit route and the published page alike),
 * and it resolves there because the origin holds the same Event row under the
 * same id — a feed or saved-in event links the row instead of copying it. So the
 * key minted in this experience is a valid key in that one, and following the
 * origin lands on the event itself, which is the only place its core fields can
 * be edited (AH-2505).
 *
 * `eventKey` is a serializeEventKey value, so a recurring event opens on the
 * same instance the viewer was looking at here.
 */
export const getOriginEventUrl = (
  origin: OriginLinkTarget,
  eventKey: string
): string | null => {
  const originUrl = getOriginUrl(origin);

  if (!originUrl) {
    return null;
  }

  // Server-built origin URLs carry no query string of their own today; checking
  // costs nothing and keeps this correct if one ever gains a param.
  const separator = originUrl.includes('?') ? '&' : '?';

  return `${originUrl}${separator}${EVENT_QUERY_PARAM}=${encodeURIComponent(eventKey)}`;
};
